import type { JourneyProfile, Material, MeResponse } from '@facecontrol/shared';
import { useMemo, useState } from 'react';
import { IconArrow } from '../components/icons';
import { CardSkeleton, MaterialCard, Photo } from '../components/MaterialCard';
import { byDate, dateDots, imageSrc, monthGenitive, monthKey, monthLong, plural } from '../lib/format';

interface Props {
  me: MeResponse | null;
  materials: Material[] | null; // null = loading
  journey?: JourneyProfile | null;
  returning: boolean;
  onOpen: (m: Material, el: HTMLElement) => void;
  /** «Моя подписка» — where the member renews */
  onSubscription?: () => void;
}

type Filter = 'all' | 'video' | 'photo';

const lessonsWord = (list: Material[]) => {
  const videos = list.filter((m) => m.contentType === 'video').length;
  const guides = list.length - videos;
  if (!guides) return plural(videos, 'видео-урок', 'видео-урока', 'видео-уроков');
  if (!videos) return plural(guides, 'фотогайд', 'фотогайда', 'фотогайдов');
  return `${plural(list.length, 'урок', 'урока', 'уроков')} · ${plural(guides, 'гайд', 'гайда', 'гайдов')}`;
};

export function Catalog({ me, materials, journey, returning, onOpen, onSubscription }: Props) {
  const groups = useMemo(() => {
    const map = new Map<string, Material[]>();
    for (const m of materials ?? []) {
      const k = monthKey(new Date(m.createdAt));
      map.set(k, [...(map.get(k) ?? []), m]);
    }
    return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1)).map(([k, list]) => [k, byDate(list)] as const);
  }, [materials]);
  const [filter, setFilter] = useState<Filter>('all');
  const [openMonth, setOpenMonth] = useState<string | null>(null);

  const current = groups[0];
  const key = current ? current[0] : monthKey(new Date());
  const items = current ? current[1] : [];
  const progress = journey?.progress ?? {};
  const done = items.filter((m) => progress[m.id]?.completedAt).length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;
  const endTime = me?.subscription.endTime;
  const daysLeft = endTime ? Math.ceil((new Date(endTime).getTime() - Date.now()) / 86_400_000) : null;
  const mixed = items.some((m) => m.contentType === 'video') && items.some((m) => m.contentType === 'photo');
  // newest first on screen; «Урок N» stays the publishing order
  const shown = [...(filter === 'all' ? items : items.filter((m) => m.contentType === filter))].reverse();
  // «Продолжить просмотр»: the furthest started-but-unfinished lesson
  const resume = useMemo(() => {
    let best: Material | null = null;
    for (const m of materials ?? []) {
      const p = journey?.progress[m.id];
      if (!p || p.completedAt || p.position <= 0) continue;
      if (!best || p.position > (journey!.progress[best.id]?.position ?? 0)) best = m;
    }
    return best;
  }, [materials, journey]);
  const archive = groups.slice(1);
  const opened = archive.find(([k]) => k === openMonth);

  return (
    <main className={`page catalog${returning ? ' is-returning' : ''}`} id="catalog">
      <section className="cat-head" aria-live="polite">
        <div>
          <p className="overline">{me?.user.firstName ? `Личный кабинет · ${me.user.firstName}` : 'Личный кабинет'}</p>
          <h1 className="h-display">Твоя программа {monthGenitive(key)}</h1>
          <p className="cat-head__meta">
            {monthLong(key)} · {materials === null ? 'загружаем программу…' : items.length ? lessonsWord(items) : 'уроки скоро'}
          </p>
        </div>
        {items.length > 0 && journey && (
          <div className="meter" role="progressbar" aria-valuemin={0} aria-valuemax={items.length} aria-valuenow={done} aria-label="Пройдено уроков месяца">
            <span className="meter__top">
              <span>
                Пройдено {done} из {items.length}
              </span>
              <span>{pct}%</span>
            </span>
            <span className="bar bar--gold">
              <i style={{ width: `${pct}%` }} />
            </span>
          </div>
        )}
      </section>
      {endTime && daysLeft !== null && daysLeft <= 3 && (
        <p className="warn">
          Доступ до {dateDots(endTime)} — продлите подписку, чтобы не потерять уроки.{' '}
          {onSubscription && (
            <button type="button" className="link" onClick={onSubscription}>
              Моя подписка <IconArrow />
            </button>
          )}
        </p>
      )}

      {resume && (
        <section className="resume" aria-labelledby="resume-h">
          <h2 id="resume-h" className="h-section">
            Продолжить просмотр
          </h2>
          <button type="button" className="band" onClick={(e) => onOpen(resume, e.currentTarget)}>
            <Photo src={imageSrc(resume.preview)} name={resume.name} eager className="band__photo photo" />
            <span className="band__body">
              <span className="overline">Ты остановилась здесь</span>
              <span className="band__title">{resume.name}</span>
              {resume.subtitle && <span className="band__sub">{resume.subtitle}</span>}
              <span className="band__progress">
                <span className="bar bar--gold">
                  <i style={{ width: `${Math.round((progress[resume.id]?.position ?? 0) * 100)}%` }} />
                </span>
                Просмотрено {Math.round((progress[resume.id]?.position ?? 0) * 100)}%
              </span>
              <span className="btn band__btn">
                Продолжить урок <IconArrow />
              </span>
            </span>
          </button>
        </section>
      )}

      <section className="lessons" aria-labelledby="lessons-h">
        <div className="sec-head">
          <h2 id="lessons-h" className="h-section">
            Уроки месяца
          </h2>
          {mixed && (
            <div className="chips" role="group" aria-label="Тип урока">
              {(
                [
                  ['all', 'Все'],
                  ['video', 'Видео'],
                  ['photo', 'Гайды'],
                ] as const
              ).map(([f, label]) => (
                <button key={f} type="button" className={`chip${filter === f ? ' is-on' : ''}`} aria-pressed={filter === f} onClick={() => setFilter(f)}>
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
        {materials === null ? (
          <div className="grid" aria-busy="true">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </div>
        ) : items.length === 0 ? (
          <div className="empty">
            <b>Пока пусто</b>
            Уроки этого месяца появятся здесь по расписанию, которое публикуется в канале. Возвращайтесь — мы всё покажем.
          </div>
        ) : (
          <div className="grid">
            {shown.map((m) => (
              <MaterialCard key={m.id} material={m} number={items.indexOf(m) + 1} progress={progress[m.id]} eager={shown.indexOf(m) < 2} onOpen={onOpen} />
            ))}
          </div>
        )}
      </section>

      {archive.length > 0 && (
        <section className="lower">
          <div className="archive">
              <h2 className="h-section">Доступный архив</h2>
              <ul className="archive__list">
                {archive.map(([k, list]) => (
                  <li key={k}>
                    <button type="button" className={`arow${openMonth === k ? ' is-open' : ''}`} aria-expanded={openMonth === k} onClick={() => setOpenMonth(openMonth === k ? null : k)}>
                      <Photo src={imageSrc(list.find((m) => m.preview)?.preview)} name={monthLong(k)} className="arow__photo photo" />
                      <span className="arow__body">
                        <span className="arow__title">{monthLong(k)}</span>
                        <span className="arow__meta">{lessonsWord(list)}</span>
                        <span className="link">
                          {openMonth === k ? 'Свернуть' : 'Открыть программу'} <IconArrow />
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
          </div>
        </section>
      )}

      {opened && (
        <section className="lessons lessons--archive" aria-label={`Программа: ${monthLong(opened[0])}`}>
          <div className="sec-head">
            <h2 className="h-section">{monthLong(opened[0])}</h2>
          </div>
          <div className="grid">
            {[...opened[1]].reverse().map((m) => (
              <MaterialCard key={m.id} material={m} number={opened[1].indexOf(m) + 1} progress={progress[m.id]} onOpen={onOpen} />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
